import { useState, useCallback, useMemo } from "react";
import {
  isLeafCondition,
  isAndCondition,
  isOrCondition,
  isNotCondition,
  isElemMatchCondition,
  type VisibilityCondition,
  type LeafCondition,
  type ItemCondition,
  type DataSourceType,
} from "@tellescope/visibility-schema";

export type LogicalOperator = "$and" | "$or";

export interface UseConditionBuilderOptions {
  initialCondition?: VisibilityCondition;
  onChange?: (condition: VisibilityCondition | undefined) => void;
}

export interface UseConditionBuilderReturn {
  /** The current condition tree */
  condition: VisibilityCondition | undefined;

  /** Set the entire condition */
  setCondition: (condition: VisibilityCondition | undefined) => void;

  /** Initialize with a root logical operator ($and or $or) */
  initializeRoot: (operator: LogicalOperator) => void;

  /** Add a leaf condition at a path */
  addLeafCondition: (path: number[], leaf: LeafCondition) => void;

  /** Add a logical group at a path */
  addLogicalGroup: (path: number[], operator: LogicalOperator) => void;

  /** Update a leaf condition at a path */
  updateLeafCondition: (path: number[], updates: Partial<LeafCondition>) => void;

  /** Remove a condition at a path */
  removeCondition: (path: number[]) => void;

  /** Toggle a logical operator between $and and $or at a path */
  toggleLogicalOperator: (path: number[]) => void;

  /** Wrap a condition in $not at a path */
  wrapInNot: (path: number[]) => void;

  /** Unwrap a $not condition at a path */
  unwrapNot: (path: number[]) => void;

  /** Initialize root as a $elemMatch group */
  initializeRootElemMatch: (source: DataSourceType) => void;

  /** Add a $elemMatch group at a path */
  addElemMatchGroup: (path: number[], source: DataSourceType) => void;

  /** Update the source of a $elemMatch at a path */
  updateElemMatchSource: (path: number[], source: DataSourceType) => void;

  /** Add an empty condition to a $elemMatch at a path */
  addElemMatchCondition: (path: number[]) => void;

  /** Update a condition within a $elemMatch at a path */
  updateElemMatchCondition: (path: number[], conditionIndex: number, updates: Partial<ItemCondition>) => void;

  /** Remove a condition within a $elemMatch at a path */
  removeElemMatchCondition: (path: number[], conditionIndex: number) => void;

  /** Clear all conditions */
  clear: () => void;

  /** Whether the condition is empty (no condition set) */
  isEmpty: boolean;
}

/**
 * Creates a default leaf condition.
 */
export function createDefaultLeafCondition(): LeafCondition {
  return {
    source: "enduser",
    field: "",
    value: "",
  };
}

/**
 * Deep clones a condition tree.
 */
function cloneCondition(condition: VisibilityCondition): VisibilityCondition {
  return JSON.parse(JSON.stringify(condition));
}

/**
 * Navigates to a position in the condition tree and returns the parent and index.
 * Path is an array of indices for navigating into $and/$or arrays.
 * For $not conditions, index 0 refers to the single child.
 */
function navigateToParent(
  root: VisibilityCondition,
  path: number[]
): { parent: VisibilityCondition; index: number } | null {
  if (path.length === 0) return null;

  let current = root;
  const parentPath = path.slice(0, -1);
  const targetIndex = path[path.length - 1];
  if (targetIndex === undefined) return null;

  for (const idx of parentPath) {
    if (isAndCondition(current)) {
      const next = current.$and[idx];
      if (!next) return null;
      current = next;
    } else if (isOrCondition(current)) {
      const next = current.$or[idx];
      if (!next) return null;
      current = next;
    } else if (isNotCondition(current)) {
      // $not has a single child, index should be 0
      if (idx !== 0) return null;
      current = current.$not;
    } else {
      return null;
    }
  }

  return { parent: current, index: targetIndex };
}

/**
 * Gets the array of children from a logical condition.
 * For $not, returns a single-element array with the child.
 */
function getChildren(condition: VisibilityCondition): VisibilityCondition[] | null {
  if (isAndCondition(condition)) return condition.$and;
  if (isOrCondition(condition)) return condition.$or;
  if (isNotCondition(condition)) return [condition.$not];
  return null;
}

/**
 * Sets the array of children on a logical condition (mutates in place).
 * For $not, only the first element is used.
 */
function setChildren(condition: VisibilityCondition, children: VisibilityCondition[]): void {
  if (isAndCondition(condition)) {
    condition.$and = children;
  } else if (isOrCondition(condition)) {
    condition.$or = children;
  } else if (isNotCondition(condition) && children.length > 0) {
    const firstChild = children[0];
    if (firstChild) {
      condition.$not = firstChild;
    }
  }
}

/**
 * Returns the node at a given path in the condition tree.
 */
function getNodeAtPath(root: VisibilityCondition, path: number[]): VisibilityCondition | null {
  if (path.length === 0) return root;
  const nav = navigateToParent(root, path);
  if (!nav) return null;
  const children = getChildren(nav.parent);
  return children?.[nav.index] ?? null;
}

/**
 * Hook for building and managing visibility conditions.
 */
export function useConditionBuilder(options: UseConditionBuilderOptions = {}): UseConditionBuilderReturn {
  const [condition, setConditionState] = useState<VisibilityCondition | undefined>(options.initialCondition);

  const setCondition = useCallback(
    (newCondition: VisibilityCondition | undefined) => {
      setConditionState(newCondition);
      options.onChange?.(newCondition);
    },
    [options.onChange]
  );

  const initializeRoot = useCallback(
    (operator: LogicalOperator) => {
      const newCondition: VisibilityCondition =
        operator === "$and" ? { $and: [createDefaultLeafCondition()] } : { $or: [createDefaultLeafCondition()] };
      setCondition(newCondition);
    },
    [setCondition]
  );

  const addLeafCondition = useCallback(
    (path: number[], leaf: LeafCondition) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);

      if (path.length === 0) {
        // Adding to root level
        const children = getChildren(newCondition);
        if (children) {
          children.push(leaf);
        }
      } else {
        const nav = navigateToParent(newCondition, path);
        if (nav) {
          const children = getChildren(nav.parent);
          if (children) {
            // Insert after the specified index
            children.splice(nav.index + 1, 0, leaf);
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const addLogicalGroup = useCallback(
    (path: number[], operator: LogicalOperator) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const newGroup: VisibilityCondition =
        operator === "$and" ? { $and: [createDefaultLeafCondition()] } : { $or: [createDefaultLeafCondition()] };

      if (path.length === 0) {
        // Adding to root level
        const children = getChildren(newCondition);
        if (children) {
          children.push(newGroup);
        }
      } else {
        const nav = navigateToParent(newCondition, path);
        if (nav) {
          const children = getChildren(nav.parent);
          if (children) {
            children.splice(nav.index + 1, 0, newGroup);
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const updateLeafCondition = useCallback(
    (path: number[], updates: Partial<LeafCondition>) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const nav = navigateToParent(newCondition, path);

      if (nav) {
        // Special handling for $not parent - it has a single direct child
        if (isNotCondition(nav.parent)) {
          if (nav.index === 0 && isLeafCondition(nav.parent.$not)) {
            (nav.parent as { $not: VisibilityCondition }).$not = { ...nav.parent.$not, ...updates };
          }
        } else {
          const children = getChildren(nav.parent);
          if (children) {
            const child = children[nav.index];
            if (child && isLeafCondition(child)) {
              const updatedChild: LeafCondition = { ...child, ...updates };
              children[nav.index] = updatedChild;
            }
          }
        }
      } else if (path.length === 0 && isLeafCondition(newCondition)) {
        // Root is a leaf
        setCondition({ ...newCondition, ...updates });
        return;
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const removeCondition = useCallback(
    (path: number[]) => {
      if (!condition) return;

      // Removing root
      if (path.length === 0) {
        setCondition(undefined);
        return;
      }

      const newCondition = cloneCondition(condition);

      // Helper to remove at a given path within the cloned tree
      const removeAtPath = (targetPath: number[]): boolean => {
        if (targetPath.length === 0) return false;

        const nav = navigateToParent(newCondition, targetPath);
        if (!nav) return false;

        const children = getChildren(nav.parent);
        if (!children) return false;

        // For $not, removing the child means removing the $not itself
        if (isNotCondition(nav.parent)) {
          // Need to remove the $not from its parent
          if (targetPath.length > 1) {
            return removeAtPath(targetPath.slice(0, -1));
          }
          return false;
        }

        children.splice(nav.index, 1);
        setChildren(nav.parent, children);

        // If no children left, remove the parent too (unless it's root)
        if (children.length === 0 && targetPath.length > 1) {
          return removeAtPath(targetPath.slice(0, -1));
        }

        return true;
      };

      removeAtPath(path);
      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const toggleLogicalOperator = useCallback(
    (path: number[]) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);

      if (path.length === 0) {
        // Toggle root
        if (isAndCondition(newCondition)) {
          setCondition({ $or: newCondition.$and });
        } else if (isOrCondition(newCondition)) {
          setCondition({ $and: newCondition.$or });
        }
        return;
      }

      const nav = navigateToParent(newCondition, path);
      if (nav) {
        // Special handling for $not parent - it has a single direct child
        if (isNotCondition(nav.parent)) {
          if (nav.index === 0) {
            const target = nav.parent.$not;
            if (isAndCondition(target)) {
              (nav.parent as { $not: VisibilityCondition }).$not = { $or: target.$and };
            } else if (isOrCondition(target)) {
              (nav.parent as { $not: VisibilityCondition }).$not = { $and: target.$or };
            }
          }
        } else {
          const children = getChildren(nav.parent);
          if (children) {
            const target = children[nav.index];
            if (target && isAndCondition(target)) {
              children[nav.index] = { $or: target.$and };
            } else if (target && isOrCondition(target)) {
              children[nav.index] = { $and: target.$or };
            }
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const wrapInNot = useCallback(
    (path: number[]) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);

      if (path.length === 0) {
        // Wrap root in $not
        setCondition({ $not: newCondition });
        return;
      }

      const nav = navigateToParent(newCondition, path);
      if (nav) {
        // Special handling for $not parent - it has a single direct child
        if (isNotCondition(nav.parent)) {
          if (nav.index === 0) {
            (nav.parent as { $not: VisibilityCondition }).$not = { $not: nav.parent.$not };
          }
        } else {
          const children = getChildren(nav.parent);
          if (children) {
            const child = children[nav.index];
            if (child) {
              children[nav.index] = { $not: child };
            }
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const unwrapNot = useCallback(
    (path: number[]) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);

      if (path.length === 0) {
        // Unwrap root $not
        if (isNotCondition(newCondition)) {
          setCondition(newCondition.$not);
        }
        return;
      }

      const nav = navigateToParent(newCondition, path);
      if (nav) {
        // Special handling for $not parent - it has a single direct child
        if (isNotCondition(nav.parent)) {
          if (nav.index === 0 && isNotCondition(nav.parent.$not)) {
            // Unwrap nested $not: $not > $not > X becomes $not > X
            (nav.parent as { $not: VisibilityCondition }).$not = nav.parent.$not.$not;
          }
        } else {
          const children = getChildren(nav.parent);
          const child = children?.[nav.index];
          if (children && child && isNotCondition(child)) {
            children[nav.index] = child.$not;
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const initializeRootElemMatch = useCallback(
    (source: DataSourceType) => {
      setCondition({
        $elemMatch: {
          source,
          conditions: [{ field: "", value: "" }],
        },
      });
    },
    [setCondition]
  );

  const addElemMatchGroup = useCallback(
    (path: number[], source: DataSourceType) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const newGroup: VisibilityCondition = {
        $elemMatch: {
          source,
          conditions: [{ field: "", value: "" }],
        },
      };

      if (path.length === 0) {
        const children = getChildren(newCondition);
        if (!children) return;
        children.push(newGroup);
      } else {
        const nav = navigateToParent(newCondition, path);
        if (!nav) return;
        const children = getChildren(nav.parent);
        if (!children) return;
        children.splice(nav.index + 1, 0, newGroup);
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const updateElemMatchSource = useCallback(
    (path: number[], source: DataSourceType) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const node = getNodeAtPath(newCondition, path);
      if (node && isElemMatchCondition(node)) {
        node.$elemMatch.source = source;
        // Reset conditions when source changes to prevent stale field/value combinations
        node.$elemMatch.conditions = [{ field: "", value: "" }];
        setCondition(newCondition);
      }
    },
    [condition, setCondition]
  );

  const addElemMatchCondition = useCallback(
    (path: number[]) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const node = getNodeAtPath(newCondition, path);
      if (node && isElemMatchCondition(node)) {
        node.$elemMatch.conditions.push({ field: "", value: "" });
        setCondition(newCondition);
      }
    },
    [condition, setCondition]
  );

  const updateElemMatchCondition = useCallback(
    (path: number[], conditionIndex: number, updates: Partial<ItemCondition>) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const node = getNodeAtPath(newCondition, path);
      if (node && isElemMatchCondition(node)) {
        const existing = node.$elemMatch.conditions[conditionIndex];
        if (existing) {
          node.$elemMatch.conditions[conditionIndex] = { ...existing, ...updates };
          setCondition(newCondition);
        }
      }
    },
    [condition, setCondition]
  );

  const removeElemMatchCondition = useCallback(
    (path: number[], conditionIndex: number) => {
      if (!condition) return;

      const newCondition = cloneCondition(condition);
      const node = getNodeAtPath(newCondition, path);
      if (node && isElemMatchCondition(node)) {
        node.$elemMatch.conditions.splice(conditionIndex, 1);
        if (node.$elemMatch.conditions.length === 0) {
          // Remove the entire $elemMatch node when all conditions are removed
          if (path.length === 0) {
            setCondition(undefined);
            return;
          }
          const nav = navigateToParent(newCondition, path);
          if (nav) {
            const children = getChildren(nav.parent);
            if (children) {
              children.splice(nav.index, 1);
            }
          }
        }
      }

      setCondition(newCondition);
    },
    [condition, setCondition]
  );

  const clear = useCallback(() => {
    setCondition(undefined);
  }, [setCondition]);

  const isEmpty = useMemo(() => condition === undefined, [condition]);

  return {
    condition,
    setCondition,
    initializeRoot,
    addLeafCondition,
    addLogicalGroup,
    updateLeafCondition,
    removeCondition,
    toggleLogicalOperator,
    wrapInNot,
    unwrapNot,
    initializeRootElemMatch,
    addElemMatchGroup,
    updateElemMatchSource,
    addElemMatchCondition,
    updateElemMatchCondition,
    removeElemMatchCondition,
    clear,
    isEmpty,
  };
}
